/**
 * QR scanner — picks the best surface available.
 *
 *   1. Inside Telegram (Bot API 6.4+) → window.Telegram.WebApp.showScanQrPopup
 *   2. Modern Chromium / Safari 16.4+ → BarcodeDetector + getUserMedia
 *   3. Anything else → reject with a clear capability error so the caller
 *      can fall back to manual entry.
 *
 * Returns a single decoded text payload, or null on user cancel.
 */

type ScanResolver = (text: string | null) => void;

interface TgWebApp {
  showScanQrPopup?: (
    p: { text?: string },
    cb: (text: string) => boolean | void,
  ) => void;
  closeScanQrPopup?: () => void;
}

interface DetectedQR { rawValue: string }
interface BarcodeDetectorLike {
  detect: (source: CanvasImageSource | HTMLVideoElement) => Promise<DetectedQR[]>;
}
type BarcodeDetectorCtor = new (opts: { formats: string[] }) => BarcodeDetectorLike;

function tgScanner(prompt: string): Promise<string | null> {
  return new Promise((resolve) => {
    const tg = (window as unknown as { Telegram?: { WebApp?: TgWebApp } })
      .Telegram?.WebApp;
    if (!tg?.showScanQrPopup) {
      resolve(null);
      return;
    }
    let settled = false;
    tg.showScanQrPopup({ text: prompt }, (text) => {
      if (settled) return true;
      settled = true;
      resolve(text || null);
      return true;
    });
  });
}

async function browserScanner(prompt: string): Promise<string | null> {
  const Detector = (window as unknown as { BarcodeDetector?: BarcodeDetectorCtor })
    .BarcodeDetector;
  if (!Detector) throw new Error("BarcodeDetector unavailable");
  const detector = new Detector({ formats: ["qr_code"] });

  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error("Camera API unavailable");
  }

  const stream = await navigator.mediaDevices.getUserMedia({
    video: { facingMode: { ideal: "environment" } },
    audio: false,
  });

  return new Promise<string | null>((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "qr-scan";

    const video = document.createElement("video");
    video.playsInline = true;
    video.muted = true;
    video.autoplay = true;
    video.srcObject = stream;

    const frame = document.createElement("div");
    frame.className = "qr-scan__frame";

    const hint = document.createElement("p");
    hint.className = "qr-scan__hint";
    hint.textContent = prompt;

    const cancel = document.createElement("button");
    cancel.className = "qr-scan__cancel";
    cancel.type = "button";
    cancel.textContent = "Cancel";

    overlay.append(video, frame, hint, cancel);
    document.body.appendChild(overlay);

    let stopped = false;
    const cleanup: ScanResolver = (text) => {
      if (stopped) return;
      stopped = true;
      for (const t of stream.getTracks()) t.stop();
      overlay.remove();
      resolve(text);
    };

    cancel.addEventListener("click", () => cleanup(null));

    // Poll every animation frame; throttle to ~5 detections/sec via a
    // running flag so we don't burn CPU on busy frames.
    let busy = false;
    async function tick(): Promise<void> {
      if (stopped) return;
      if (!busy && video.readyState >= 2) {
        busy = true;
        try {
          const found = await detector.detect(video);
          if (found && found.length > 0 && found[0]!.rawValue) {
            cleanup(found[0]!.rawValue);
            return;
          }
        } catch {
          // transient — keep polling
        } finally {
          busy = false;
        }
      }
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  });
}

export interface ScanOptions {
  prompt?: string;
}

/**
 * Open a QR scanner UI and resolve with the decoded text, or null on
 * cancel. Throws only when NO scanner surface is reachable at all
 * (caller should fall back to manual entry then).
 */
export async function scanQR(opts: ScanOptions = {}): Promise<string | null> {
  const prompt = opts.prompt ?? "Point at the QR code";

  // 1) Telegram host first — best UX inside the Mini App.
  const tg = (window as unknown as { Telegram?: { WebApp?: TgWebApp } })
    .Telegram?.WebApp;
  if (tg?.showScanQrPopup) {
    return tgScanner(prompt);
  }

  // 2) Browser fallback via BarcodeDetector + getUserMedia.
  const hasDetector = !!(window as unknown as { BarcodeDetector?: unknown })
    .BarcodeDetector;
  const hasCamera = !!navigator.mediaDevices?.getUserMedia;
  if (hasDetector && hasCamera) {
    return browserScanner(prompt);
  }

  throw new Error(
    "QR scan needs Telegram 9.5+, or a browser with BarcodeDetector and camera permission.",
  );
}
